<template>
  <div class="p-8 bg-[#FAFAFA] min-h-screen font-sans text-[#222222]">
    <!-- Header -->
    <header class="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
      <div>
        <h1 class="text-3xl font-extrabold tracking-tight">Écrans de l'agence</h1>
        <p class="text-gray-500 font-medium">Suivi des devices Screen et navigation vers l'édition du contenu.</p>
      </div>
      <button @click="showAddModal = true" class="bg-blue-500 text-white px-6 py-3 rounded-2xl font-bold text-sm shadow-lg shadow-blue-200 hover:scale-105 transition-transform flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 4v16m8-8H4" /></svg>
        Nouveau Screen
      </button>
    </header>

    <!-- Recherche -->
    <div class="bg-white p-4 rounded-[2rem] shadow-sm border border-gray-100 mb-8 flex flex-wrap gap-4 items-center">
      <div class="flex-1 min-w-[300px] relative">
        <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </span>
        <input type="text" v-model="search" placeholder="Rechercher un screen par ID ou status..." class="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-dimotec-orange/20 transition-all text-sm">
      </div>
    </div>

    <!-- Table Screens -->
    <div class="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 overflow-hidden border border-gray-100">
      <template v-if="filteredScreens.length === 0">
        <div class="p-10 text-center text-gray-400 font-bold">
          🚀 Aucun screen trouvé
        </div>
      </template>

      <template v-else>
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50/50 text-left text-[11px] font-black uppercase tracking-[0.15em] text-gray-400 border-b border-gray-100">
              <th class="px-8 py-6">Agence</th>
              <th class="px-8 py-6">ID Screen</th>
              <th class="px-8 py-6">Nom Screen</th>
              <th class="px-6 py-6 text-center">Statut</th>
              <th class="px-6 py-6">Dernière MAJ</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr v-for="screen in filteredScreens" :key="screen.id" class="hover:bg-gray-50/50 transition-colors group">
              <td class="px-8 py-6 font-bold">{{ screen.agencyName }}</td>
              <td class="px-8 py-6 font-bold">{{ screen.id }}</td>
              <td class="px-8 py-6 font-bold">{{ screen.name }}</td>
              <td class="px-6 py-6 text-center">
                <span :class="statusClasses(screen.status)" class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border">
                  {{ screen.statusLabel }}
                </span>
              </td>
              <td class="px-6 py-6">{{ screen.lastUpdate }}</td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>

    <!-- Modal Ajout Screen -->
    <div v-if="showAddModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-white w-full max-w-xl rounded-[2.5rem] p-10 shadow-2xl">
        <h2 class="text-2xl font-black mb-2">Ajouter un Screen</h2>
        <p class="text-gray-500 mb-8 text-sm font-medium">Configurez le screen pour l'agence.</p>
        <div class="space-y-4">
          <input v-model="newScreen.id" type="text" placeholder="ID du screen" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20">
          <select v-model="newScreen.status" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20">
            <option value="active">Actif</option>
            <option value="maintenance">Maintenance</option>
            <option value="suspended">Suspendu</option>
          </select>
        </div>
        <div class="flex gap-4 mt-8">
          <button @click="showAddModal = false" class="flex-1 py-3 font-bold text-gray-400">Annuler</button>
          <button @click="saveNewScreen" class="flex-1 py-3 bg-blue-500 text-white rounded-xl font-bold shadow-lg shadow-blue-100">Créer Screen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';

export default {
  data() {
    return {
      screens: [],
      search: '',
      showAddModal: false,
      newScreen: { id: '', status: 'active' },
    };
  },
  computed: {
    filteredScreens() {
      if (!this.search) return this.screens;
      return this.screens.filter(s => s.id.includes(this.search) || s.statusLabel.toLowerCase().includes(this.search.toLowerCase()));
    }
  },
  mounted() {
    this.fetchScreens();
  },
  methods: {
    async fetchScreens() {
      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch("https://api.datafuse.fr/api/super-admin/screens", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Erreur récupération screens");
        const data = await res.json();
        console.log(data);
        this.screens = data.map(s => ({
          id: s.id,
          name: s.name,
          identifier: s.identifier,
          agencyName: s.agencyName,
          agencyId: s.agencyId,
          online: s.online,
          status: s.online ? "active" : "inactive",
          statusLabel: s.online ? "Online" : "Offline",
          lastUpdate: new Date(s.lastUpdate).toLocaleDateString()
        }));
      } catch (err) {
        console.error(err);
        toast.error("Impossible de récupérer les screens");
      }
    },
    statusClasses(status) {
      switch(status) {
        case 'active': return 'bg-green-50 text-green-600 border-green-100';
        case 'maintenance': return 'bg-yellow-50 text-yellow-600 border-yellow-100';
        default: return 'bg-red-50 text-red-600 border-red-100';
      }
    },
    saveNewScreen() {
      const screen = {
        id: this.newScreen.id,
        status: this.newScreen.status,
        statusLabel: this.newScreen.status === 'active' ? 'Online' : this.newScreen.status === 'maintenance' ? 'Maintenance' : 'Offline',
        lastUpdate: new Date().toLocaleDateString(),
        agencyName: 'Agence Demo' // à remplacer par la vraie agence
      };
      this.screens.push(screen);
      toast.success("Screen créé !");
      this.showAddModal = false;
      this.newScreen = { id: '', status: 'active' };
    }
  }
}
</script>
